import { PlusIcon } from "lucide-react";
import { useId } from "react";

import { cn } from "@/lib/utils";

export interface SkillFileTreeEntry {
    clientKey: string;
    name: string;
    isDirty: boolean;
    isInvalid: boolean;
    brokenReferenceCount?: number;
}

export interface SkillFileTreeReferenceEntry extends SkillFileTreeEntry {
    isPlaceholder: boolean;
}

export interface SkillFileTreeOutlineHeading {
    label: string;
    index: number;
}

export interface SkillFileTreeOutline {
    headings: readonly SkillFileTreeOutlineHeading[];
    onJump: (heading: SkillFileTreeOutlineHeading) => void;
}

export interface SkillFileTreeProps {
    indexEntry: SkillFileTreeEntry;
    referenceEntries: SkillFileTreeReferenceEntry[];
    selectedClientKey: string;
    onSelect: (clientKey: string) => void;
    onAddReference: () => void;
    outline: SkillFileTreeOutline;
}

type FileMark = "invalid" | "broken" | "unsaved";

const FILE_MARKS: Record<
    FileMark,
    { label: (brokenReferenceCount: number) => string; className: string }
> = {
    invalid: { label: () => "invalid", className: "text-destructive" },
    broken: {
        label: (count) => `${count} invalid`,
        className: "text-destructive",
    },
    unsaved: { label: () => "unsaved", className: "text-brand-copper" },
};

// A name or content error blocks the save outright, so it outranks a broken
// target, which does not.
function fileMarkKey(
    isInvalid: boolean,
    brokenReferenceCount: number,
    isDirty: boolean
): FileMark | null {
    if (isInvalid) return "invalid";
    if (brokenReferenceCount > 0) return "broken";
    if (isDirty) return "unsaved";
    return null;
}

const GROUP_LABEL_CLASS =
    "text-muted-foreground flex items-center justify-between px-2 text-[11px] font-medium tracking-wider uppercase";

const ROW_CLASS =
    "focus-visible:ring-ring/50 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors outline-none focus-visible:ring-3";

const ROW_IDLE_CLASS =
    "text-muted-foreground hover:bg-muted hover:text-foreground";

const OUTLINE_ROW_CLASS =
    "focus-visible:ring-ring/50 text-muted-foreground hover:bg-muted hover:text-foreground block w-full truncate rounded-md px-2 py-1 text-left text-xs outline-none transition-colors focus-visible:ring-2";

const MAX_OUTLINE_ROWS = 200;

function FileRow({
    label,
    secondary,
    isUntitled = false,
    isDirty,
    isInvalid,
    brokenReferenceCount,
    isSelected,
    onSelect,
}: {
    label: string;
    secondary?: string;
    isUntitled?: boolean;
    isDirty: boolean;
    isInvalid: boolean;
    brokenReferenceCount: number;
    isSelected: boolean;
    onSelect: () => void;
}) {
    const markKey = fileMarkKey(isInvalid, brokenReferenceCount, isDirty);
    const mark = markKey && FILE_MARKS[markKey];

    return (
        <button
            type="button"
            onClick={onSelect}
            aria-current={isSelected ? "true" : undefined}
            className={cn(
                ROW_CLASS,
                isSelected
                    ? "bg-primary/5 text-foreground font-medium"
                    : ROW_IDLE_CLASS
            )}
        >
            <span className="min-w-0 flex-1">
                <span
                    className={cn("block truncate", isUntitled && "italic")}
                    title={isUntitled ? undefined : label}
                >
                    {label}
                </span>
                {secondary && (
                    <span className="text-muted-foreground block truncate text-[11px]">
                        {secondary}
                    </span>
                )}
            </span>
            {mark && (
                <span className={cn("shrink-0 text-[11px]", mark.className)}>
                    {mark.label(brokenReferenceCount)}
                </span>
            )}
        </button>
    );
}

export function SkillFileTree({
    indexEntry,
    referenceEntries,
    selectedClientKey,
    onSelect,
    onAddReference,
    outline,
}: SkillFileTreeProps) {
    const groupId = useId();
    const referencesHeadingId = `${groupId}-references`;

    const countedReferences = referenceEntries.filter(
        (entry) => !entry.isPlaceholder
    ).length;
    const visibleOutlineHeadings = outline.headings.slice(0, MAX_OUTLINE_ROWS);
    const hiddenOutlineCount =
        outline.headings.length - visibleOutlineHeadings.length;

    return (
        <div className="flex h-full min-h-0 w-full flex-col py-3">
            <nav
                aria-label="Skill files"
                className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-2"
            >
                <div className="space-y-1">
                    <FileRow
                        label="Instructions"
                        secondary={indexEntry.name}
                        isDirty={indexEntry.isDirty}
                        isInvalid={indexEntry.isInvalid}
                        brokenReferenceCount={
                            indexEntry.brokenReferenceCount ?? 0
                        }
                        isSelected={selectedClientKey === indexEntry.clientKey}
                        onSelect={() => onSelect(indexEntry.clientKey)}
                    />

                    {selectedClientKey === indexEntry.clientKey &&
                        outline.headings.length > 0 && (
                            <div
                                role="group"
                                aria-label="Instructions outline"
                                className="border-border ml-4 space-y-0.5 border-l pl-2"
                            >
                                {visibleOutlineHeadings.map((heading) => (
                                    <button
                                        key={heading.index}
                                        type="button"
                                        title={heading.label}
                                        onClick={() => outline.onJump(heading)}
                                        className={OUTLINE_ROW_CLASS}
                                    >
                                        {heading.label}
                                    </button>
                                ))}
                                {hiddenOutlineCount > 0 && (
                                    <p className="text-muted-foreground px-2 py-1 text-xs">
                                        +{hiddenOutlineCount} more
                                    </p>
                                )}
                            </div>
                        )}
                </div>

                <div className="space-y-1">
                    <h2 id={referencesHeadingId} className={GROUP_LABEL_CLASS}>
                        <span>References</span>
                        <span className="tabular-nums">
                            {countedReferences}
                        </span>
                    </h2>

                    <div
                        role="group"
                        aria-labelledby={referencesHeadingId}
                        className="space-y-0.5"
                    >
                        {referenceEntries.map((entry) => {
                            const isUntitled = entry.name.trim() === "";
                            return (
                                <FileRow
                                    key={entry.clientKey}
                                    label={
                                        isUntitled ? "(untitled)" : entry.name
                                    }
                                    isUntitled={isUntitled}
                                    isDirty={
                                        entry.isDirty && !entry.isPlaceholder
                                    }
                                    isInvalid={
                                        entry.isInvalid && !entry.isPlaceholder
                                    }
                                    brokenReferenceCount={
                                        entry.isPlaceholder
                                            ? 0
                                            : (entry.brokenReferenceCount ?? 0)
                                    }
                                    isSelected={
                                        selectedClientKey === entry.clientKey
                                    }
                                    onSelect={() => onSelect(entry.clientKey)}
                                />
                            );
                        })}
                    </div>

                    <button
                        type="button"
                        onClick={onAddReference}
                        className={cn(ROW_CLASS, ROW_IDLE_CLASS)}
                    >
                        <PlusIcon className="size-3.5 shrink-0" />
                        Add reference
                    </button>

                    {referenceEntries.length === 0 && (
                        <p className="text-muted-foreground px-2 text-xs leading-[1.55]">
                            No references yet. Add one realistic example per
                            document or policy type, then @-mention it from the
                            instructions.
                        </p>
                    )}
                </div>
            </nav>
        </div>
    );
}
